import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, ButtonBase, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import SortableList from './SortableList';
import { categoryHiddenNote } from './labels';

/** The category list down the left of the Dishes tab. Drag a handle to reorder. */
const CategoryRail = ({ categories, selectedId, onSelect, onReorder, onAdd, counts, filtering }) => (
  <Paper
    variant="outlined"
    component="nav"
    aria-label="Categories"
    sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}
  >
    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
        Categories
      </Typography>
      <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>
        Add
      </Button>
    </Box>
    <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
      {categories.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
          No categories yet.
        </Typography>
      )}
      <SortableList
        type="editor-category"
        items={categories}
        getKey={(category) => category.id}
        onReorder={onReorder}
        disabled={filtering}
        renderItem={(category, index, { rowRef, handleRef, isDragging }) => {
          const selected = category.id === selectedId;
          const count = counts[category.id];
          const hidden = categoryHiddenNote(category);
          const dimmed = filtering && !count;
          return (
            <Box
              ref={rowRef}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 1.5,
                mb: 0.25,
                opacity: isDragging ? 0.4 : dimmed ? 0.45 : 1,
                bgcolor: selected ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                '&:hover .rail-handle': { opacity: 1 },
              }}
            >
              <Box
                ref={handleRef}
                className="rail-handle"
                aria-label={`Drag to reorder ${category.name}`}
                sx={{
                  display: 'flex',
                  pl: 0.5,
                  color: 'text.disabled',
                  cursor: filtering ? 'default' : 'grab',
                  opacity: { xs: 1, md: selected ? 1 : 0.35 },
                  visibility: filtering ? 'hidden' : 'visible',
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
              <ButtonBase
                onClick={() => onSelect(category.id)}
                aria-current={selected ? 'true' : undefined}
                sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', py: 1, pr: 1.5, pl: 0.5, borderRadius: 1.5 }}
              >
                {category.colorHex && (
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: category.colorHex, mr: 1, flexShrink: 0 }} />
                )}
                <Typography
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, fontWeight: selected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {category.name || 'Untitled'}
                </Typography>
                {hidden && (
                  <Tooltip title={hidden}>
                    <VisibilityOffOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }} />
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {count ?? 0}
                </Typography>
              </ButtonBase>
            </Box>
          );
        }}
      />
    </Box>
    {filtering && (
      <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        Clear the search or filter to reorder.
      </Typography>
    )}
  </Paper>
);

CategoryRail.propTypes = {
  categories: PropTypes.array.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  counts: PropTypes.object.isRequired,
  filtering: PropTypes.bool,
};

export default CategoryRail;
